import { ChangeDetectionStrategy, Component, computed, inject, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { SeoService } from '../../core/seo/seo.service';
import { RouterLink } from '@angular/router';
import { PropertyDataProvider } from '../../core/data/property-data.provider';
import { CitySummary } from '../../core/models/domain.models';
import { SparklineComponent } from '../../shared/charts.components';
import { Bg2Pipe, BgIntPipe, EurM2Pipe, PctPipe } from '../../shared/format.pipes';
import { DeltaChipComponent, MarketNoticeComponent, PageHeaderComponent } from '../../shared/ui.components';

type SortKey = 'price' | 'yoy' | 'qoq' | 'dom' | 'tx' | 'yield' | 'inventory' | 'overheating';

@Component({
  selector: 'app-cities-page',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    RouterLink,
    PageHeaderComponent,
    MarketNoticeComponent,
    DeltaChipComponent,
    SparklineComponent,
    EurM2Pipe,
    BgIntPipe,
    Bg2Pipe,
    PctPipe,
  ],
  templateUrl: './cities.page.html',
  styleUrl: './cities.page.scss',
})
export class CitiesPage {
  private readonly data = inject(PropertyDataProvider);

  readonly sort = signal<SortKey>('price');
  readonly filterType = signal<'all' | 'district' | 'other'>('all');
  readonly searchQuery = signal<string>('');

  private readonly overview = toSignal(this.data.overview('month'));

  readonly totalCount = computed(() => this.overview()?.cities.length ?? 0);
  readonly districtCount = computed(
    () => this.overview()?.cities.filter((c) => c.city.isDistrictCenter ?? true).length ?? 0,
  );
  readonly otherCount = computed(
    () => this.overview()?.cities.filter((c) => c.city.isDistrictCenter === false).length ?? 0,
  );

  protected readonly leadText = computed(() => {
    const total = this.totalCount();
    const visible = this.cities()?.length ?? 0;
    const countLabel = visible === total ? `${total} пазара` : `${visible} от ${total} пазара`;
    return (
      `${countLabel} рамо до рамо: цена, ръст (ГоГ и КоК), дни на пазара, обем на сделките, ` +
      'доходност и прегряване. Сортирай по всяка колона и филтрирай по категория или търсене.'
    );
  });

  readonly cities = computed<CitySummary[] | undefined>(() => {
    const o = this.overview();
    if (!o) return undefined;

    let list = o.cities;
    const filter = this.filterType();
    if (filter === 'district') {
      list = list.filter((c) => c.city.isDistrictCenter ?? true);
    } else if (filter === 'other') {
      list = list.filter((c) => c.city.isDistrictCenter === false);
    }

    const query = this.searchQuery().trim().toLowerCase();
    if (query) {
      list = list.filter(
        (c) =>
          c.city.name.toLowerCase().includes(query) ||
          c.city.slug.toLowerCase().includes(query),
      );
    }

    const key = this.sort();
    const val = (c: CitySummary): number => {
      switch (key) {
        case 'price': return isFinite(c.medianEurPerM2) ? c.medianEurPerM2 : -Infinity;
        case 'yoy': return c.yoyPct ?? -Infinity;
        case 'qoq': return c.qoqPct ?? -Infinity;
        case 'dom': return c.domDays ?? -Infinity;
        case 'tx': return c.transactionsYoYPct ?? -Infinity;
        case 'yield': return c.rentalYieldPct ?? -Infinity;
        case 'inventory': return c.activeListings;
        case 'overheating': return c.overheatingScore ?? -Infinity;
      }
    };
    return [...list].sort((a, b) => val(b) - val(a));
  });

  constructor(seo: SeoService) {
    seo.set({
      title: 'Цени на имоти по градове в България 2026 — Класация | Имотомер',
      description:
        'Сравнение и пазарна статистика за 75 български града: офертни цени на кв.м, годишен ръст (ГоГ), сделки от Агенция по вписванията, наеми, доходност и прегряване.',
      path: '/gradove',
      keywords: [
        'цени на имоти по градове',
        'класация градове имоти',
        'цени на кв метър българия',
        'статистика имоти 2026',
        'сделки с имоти градове',
        'имотен пазар българия',
        'имотомер',
        'imotomer',
      ],
      jsonLd: {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: [
          {
            '@type': 'ListItem',
            position: 1,
            name: 'Начало',
            item: 'https://real-estate.tur92.com/',
          },
          {
            '@type': 'ListItem',
            position: 2,
            name: 'Градове',
            item: 'https://real-estate.tur92.com/gradove',
          },
        ],
      },
    });
  }

  sortBy(key: SortKey): void {
    this.sort.set(key);
  }
}
